import React from 'react'
import {ColorSquare, Loading} from '@therms/atalaya'
import {
  DateTime,
  useCurrentOrganization,
  useCurrentUser,
  useToggle,
} from '@therms/react-modules'
import {useCurrentUserAnPCheck} from '@src/modules/Users/shared/hooks/useCurrentUserAnPCheck'
import {StatusGeoTracking} from '@src/modules/Users/shared/Layout/CurrentUserStatusInteractiveCard/components/StatusGeoTracking'
import {EmergencySOS} from '@src/modules/Users/shared/Layout/CurrentUserStatusInteractiveCard/components/EmergencySOS'
import classNames from 'classnames'
import {SetUserStatusModal} from '@src/modules/Users/shared/SetUserStatusModal'
import {CurrentUserShiftStatusInteractiveCard} from '@src/modules/Scheduling/shared/Layout/CurrentUserShiftStatusInteractiveCard'

function CurrentUserStatusInteractiveCard() {
  const [showSetUserStatusModal, toggleSetUserStatusModal] = useToggle(false)

  const currentUser = useCurrentUser()
  const status = currentUser?.status
  const {modules} = useCurrentOrganization()

  const canChangeStatus = useCurrentUserAnPCheck(
    ({anp}) =>
      currentUser.id === currentUser?.id ||
      anp.role.ops.permissions.changeOtherUserStatus,
  )

  if (!currentUser) return <Loading overlay />

  return (
    <>
      {currentUser.schedulingUser && (
        <div className="mb-xxs">
          <CurrentUserShiftStatusInteractiveCard />
        </div>
      )}

      {!status?.shiftId && (
        <div>
          {status ? (
            <div
              className={classNames(
                {'cursor-pointer': canChangeStatus},
                'bg-surface-subtle hover:bg-surface-strong focus:bg-surface-strong active:bg-surface-subtle rounded-md transition-colors',
              )}
              onClick={canChangeStatus ? toggleSetUserStatusModal : undefined}
            >
              <div className="px-xs pt-xs text-color-subtle flex justify-center text-end text-xs">
                <span className="mr-xxs">Set</span>
                <DateTime
                  toggleRelativeOnClick={false}
                  relative
                  timestamp={status.setAtDate}
                />
              </div>

              <div className="space-y-xxs pb-xs flex flex-col items-center">
                <div
                  className="text-color-strong w-full text-center font-medium"
                  style={{
                    borderLeft: `3px solid${status.color}`,
                    borderRight: `3px solid${status.color}`,
                  }}
                >
                  {status.title}
                </div>

                {currentUser?.status && currentUser?.position && (
                  <div className="space-x-xxs flex items-center">
                    <ColorSquare color={currentUser.position.color} />{' '}
                    <span className="text-sm">
                      {currentUser.position.title}
                    </span>
                  </div>
                )}

                {!!currentUser.physicalActivity && (
                  <div className="text-color-subtle text-sm font-bold capitalize">
                    {currentUser.physicalActivity.activity}
                  </div>
                )}
              </div>

              {status?.geoTracking && (
                <div className="px-xs pb-xs text-color-subtle text-sm">
                  <StatusGeoTracking />
                </div>
              )}
            </div>
          ) : (
            <div
              className="bg-main-faded p-sm hover:bg-main focus:bg-main active:bg-main-faded group cursor-pointer rounded text-center transition-colors"
              onClick={canChangeStatus ? toggleSetUserStatusModal : undefined}
            >
              <span className="text-color-main active:text-color-main group-hover:text-color-semantic group-focus:text-color-semantic font-bold transition-colors">
                Set Duty Status
              </span>
            </div>
          )}
        </div>
      )}

      {!!modules.sos && !!status && (
        <div className="mt-xxs">
          <EmergencySOS />
        </div>
      )}

      {showSetUserStatusModal && (
        <SetUserStatusModal
          closeHandler={() => {
            toggleSetUserStatusModal()
          }}
          onComplete={() => {
            toggleSetUserStatusModal()
          }}
          userId={currentUser.id}
        />
      )}
    </>
  )
}

export {CurrentUserStatusInteractiveCard}
